import {
    AttendanceChangeRequest,
    AttendanceChangeRequestStatus,
    AttendanceChangeSet,
    AttendanceEventSnapshot
} from "types/attendance"

type EditedEvent = NonNullable<AttendanceChangeSet["events"]>[number]

export type ChangeDiffRow =
    | {kind: "unchanged"; event: AttendanceEventSnapshot}
    | {kind: "edited"; before: AttendanceEventSnapshot; after: EditedEvent}
    | {kind: "added"; event: NonNullable<AttendanceChangeSet["addEvents"]>[number]}
    | {kind: "removed"; event: AttendanceEventSnapshot}
    // the request edits an event that has since been deleted
    | {kind: "missing"; event: EditedEvent}

const normalizeMessage = (message?: string | null) => message || undefined

// the time picker only has minute precision, so ignore seconds when comparing
const toMinute = (iso: string) => Math.floor(new Date(iso).getTime() / 60000)

const isSameEvent = (
    a: {eventType: string; createdAt: string; message?: string | null},
    b: {eventType: string; createdAt: string; message?: string | null}
) =>
    a.eventType === b.eventType &&
    toMinute(a.createdAt) === toMinute(b.createdAt) &&
    normalizeMessage(a.message) === normalizeMessage(b.message)

const byTime = (a: {createdAt: string}, b: {createdAt: string}) =>
    new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()

// Events the request is compared against: live events while pending, snapshot once decided.
export const getBaseEvents = (request: AttendanceChangeRequest) =>
    request.status === AttendanceChangeRequestStatus.Pending ? request.currentEvents ?? [] : request.originalEvents

export function buildDiffRows(base: AttendanceEventSnapshot[], changes: AttendanceChangeSet): ChangeDiffRow[] {
    const deleted = new Set(changes.deleteEventIds ?? [])
    const edits = new Map((changes.events ?? []).map((e) => [e.id, e]))
    const baseIds = new Set(base.map((e) => e.id))

    const rows: (ChangeDiffRow & {createdAt: string})[] = base.map((event) => {
        const edit = event.id == null ? undefined : edits.get(event.id)
        if (event.id != null && deleted.has(event.id)) {
            return {kind: "removed", event, createdAt: event.createdAt}
        }
        if (edit && !isSameEvent(edit, event)) {
            return {kind: "edited", before: event, after: edit, createdAt: edit.createdAt}
        }
        return {kind: "unchanged", event, createdAt: event.createdAt}
    })

    ;(changes.events ?? [])
        .filter((e) => !baseIds.has(e.id))
        .forEach((event) => rows.push({kind: "missing", event, createdAt: event.createdAt}))
    ;(changes.addEvents ?? []).forEach((event) => rows.push({kind: "added", event, createdAt: event.createdAt}))

    return rows.sort(byTime).map(({createdAt: _createdAt, ...row}) => row as ChangeDiffRow)
}
